<template>
  <div class="position-relative h-100 w-100">
    <div class="map-header position-absolute text-light">
      <h2 class="h3 m-0"><span>Day 04 - </span>Hexagons</h2>
    </div>
    <dl class="map-source position-absolute d-flex">
      <dt class="me-1">Source:</dt>
      <dd class="text-break">
        <a href="https://scottishsquirrels.org.uk/" target="_blank"
          >https://scottishsquirrels.org.uk/</a
        >
      </dd>
    </dl>
    <div id="control-panel" class="d-none d-sm-block">
      <h2 class="h5">Squirrel Sightings</h2>
      <div class="mb-1">
        <div class="form-check form-check-inline">
          <input
            class="form-check-input"
            type="radio"
            id="grey"
            name="species"
            value="Grey"
            v-model="species"
          />
          <label class="form-check-label" for="grey">Grey</label>
        </div>
        <div class="form-check form-check-inline">
          <input
            class="form-check-input"
            type="radio"
            id="red"
            name="species"
            value="Red"
            v-model="species"
          />
          <label class="form-check-label" for="red">Red</label>
        </div>
      </div>
      <div class="mb-1">
        <label class="form-label">Radius</label>
        <input
          id="radius"
          type="range"
          class="form-range"
          min="500"
          max="10000"
          step="500"
          value="1000"
        />
        <span id="radius-value"></span>
      </div>
      <div class="mb-1">
        <label class="form-label">Coverage</label>
        <input
          id="coverage"
          class="form-range"
          type="range"
          min="0"
          max="1"
          step="0.1"
          value="1"
        />
        <span id="coverage-value"></span>
      </div>
      <div>
        <label class="form-label">Upper Percentile</label>
        <input
          id="upperPercentile"
          class="form-range"
          type="range"
          min="90"
          max="100"
          step="1"
          value="100"
        />
        <span id="upperPercentile-value"></span>
      </div>
    </div>
    <div ref="map-root" class="basemap"></div>
  </div>
</template>

<script>
import mapboxgl from "mapbox-gl";
import "mapbox-gl/src/css/mapbox-gl.css";
import { MapboxLayer } from "@deck.gl/mapbox";
import { HexagonLayer } from "@deck.gl/aggregation-layers";
import * as d3 from "d3";

export default {
  data() {
    return {
      accessToken:
        "pk.eyJ1Ijoia21hcnNoYWxsIiwiYSI6ImNrdXdudGlpODI2ejkydXF2bXcyM3poaWQifQ.hyBCr6uwWlDGMGyVmuKCIw",
      map: null,
      species: "Grey",
      redSquirrelLayer: null,
      greySquirrelLayer: null,
    };
  },
  watch: {
    species() {
      this.setLayerVisibility();
    },
  },
  methods: {
    initMap() {
      mapboxgl.accessToken = this.accessToken;

      this.map = new mapboxgl.Map({
        container: this.$refs["map-root"],
        style: "mapbox://styles/mapbox/dark-v10?optimize=true",
        center: [-3.0902637, 56.530744],
        zoom: 6,
        pitch: 40.5,
        antialias: true,
      });

      this.loadLayers();
      this.map.scrollZoom.disable();
    },
    setLayerVisibility() {
      if (this.species === "Red") {
        this.map.setLayoutProperty("red-squirrels", "visibility", "visible");
        this.map.setLayoutProperty("grey-squirrels", "visibility", "none");
      } else {
        this.map.setLayoutProperty("red-squirrels", "visibility", "none");
        this.map.setLayoutProperty("grey-squirrels", "visibility", "visible");
      }
    },
    loadLayers() {
      //Create the deck.gl hexagon layer and style for the data
      var OPTIONS = ["radius", "coverage", "upperPercentile"];
      var COLOR_RANGE = [
        [1, 152, 189],
        [73, 227, 206],
        [216, 254, 181],
        [254, 237, 177],
        [254, 173, 84],
        [209, 55, 78],
      ];
      var LIGHT_SETTINGS = {
        lightsPosition: [
          -0.144528, 49.739968, 8000, -3.807751, 54.104682, 8000,
        ],
        ambientRatio: 0.4,
        diffuseRatio: 0.6,
        specularRatio: 0.2,
        lightsStrength: [0.8, 0.0, 0.8, 0.0],
        numberOfLights: 2,
      };

      //Add the deck.gl Custom Layer to the map once the Mapbox map loads
      this.map.on("style.load", () => {
        this.redSquirrelLayer = new MapboxLayer({
          type: HexagonLayer,
          id: "red-squirrels",
          data: d3.csv("./data/red_squirrels.csv"),
          radius: 1000,
          coverage: 1,
          upperPercentile: 100,
          colorRange: COLOR_RANGE,
          elevationRange: [0, 1000],
          elevationScale: 250,
          extruded: true,
          getPosition: (d) => [Number(d.lng), Number(d.lat)],
          lightSettings: LIGHT_SETTINGS,
          opacity: 1,
        });

        this.greySquirrelLayer = new MapboxLayer({
          type: HexagonLayer,
          id: "grey-squirrels",
          data: d3.csv("./data/grey_squirrels.csv"),
          radius: 1000,
          coverage: 1,
          upperPercentile: 100,
          colorRange: COLOR_RANGE,
          elevationRange: [0, 1000],
          elevationScale: 250,
          extruded: true,
          getPosition: (d) => [Number(d.lng), Number(d.lat)],
          lightSettings: LIGHT_SETTINGS,
          opacity: 1,
        });

        // Add the deck.gl hex layer below labels in the Mapbox map
        this.map.addLayer(this.redSquirrelLayer, "waterway-label");
        this.map.addLayer(this.greySquirrelLayer, "waterway-label");
        this.setLayerVisibility();
      });

      // Add sliders to change the layer's settings based on user input
      OPTIONS.forEach((key) => {
        document.getElementById(key).onchange = (evt) => {
          var value = Number(evt.target.value);
          document.getElementById(key + "-value").innerHTML = value;
          if (this.redSquirrelLayer) {
            this.redSquirrelLayer.setProps({
              [key]: value,
            });
          }
          if (this.greySquirrelLayer) {
            this.greySquirrelLayer.setProps({
              [key]: value,
            });
          }
        };
      });
    },
  },
  mounted() {
    this.initMap();
  },
};
</script>

<style lang="scss" scoped>
@import "../assets/scss/map-shared.scss";

#control-panel {
  position: absolute;
  background: #fff;
  color: #191919;
  top: 0;
  right: 0;
  width: 200px;
  padding: 20px;
  font-size: 12px;
  line-height: 1.5;
  z-index: 1;
}
</style>
